import {get} from "lodash-es";

import * as auth from "@client/features/auth";
import * as dialogs from "@client/features/dialogs";
import {notify} from "@client/features/notifications";
import * as orm from "@client/features/orm";

import * as analytics from "@client/shared/analytics";
import * as utils from "@client/utils";

import * as hardware from "@client/hardware";
import type {Action, AppDispatch, GetState, RootState} from "@client/store";
import {models, server} from "@reactivated";

const CENTS = 100;

export type Actions =
    | {
          type: "LOAD_STRIPE_REQUEST";
      }
    | {
          type: "NEEDS_SUBSCRIPTION";
      }
    | {
          type: "SUBSCRIBING";
      }
    | {
          type: "ERROR_SUBSCRIBING";
      }
    | {
          type: "LOAD_STRIPE_SUCCESS";
      }
    | {
          type: "SHOW_PAYPAL";
      }
    | {
          type: "LOAD_APPLE_SUBSCRIPTIONS";
      };

const initialState = {
    isShowingPayPal: false,
    isLoaded: false,
};

export const reducer = (state = initialState, action: Action) => {
    switch (action.type) {
        case "SHOW_PAYPAL": {
            return utils.assign(state, {
                isShowingPayPal: true,
            });
        }
        case "LOAD_APPLE_SUBSCRIPTIONS": {
            return utils.assign(state, {
                isLoaded: true,
            });
        }
        case "LOAD_STRIPE_SUCCESS": {
            return utils.assign(state, {
                isLoaded: true,
            });
        }
        default: {
            return state;
        }
    }
};

const showPayPal = () => ({
    type: "SHOW_PAYPAL",
});

const confirmAndDeleteProfile =
    (profile: server.core.rpc.Profile.output, isApple: boolean) =>
    async (dispatch: AppDispatch) => {
        const confirmed = await dispatch(
            dialogs.confirm({
                message: "Are you sure?",
                body:
                    "Your Joy account will be deleted forever." +
                    (profile.user.is_subscription_active && isApple
                        ? "\n\nYou have an active subscription through Apple.\n\nApple will not allow us to cancel it for you.\n\nClick Manage Subscriptions to cancel it.\n\nOr just delete the app and you will be prompted to unsubscribe."
                        : ""),
            }),
        );

        if (confirmed) {
            const result = await server.core.rpc.delete_profile();

            if (result.type != "success") {
                dispatch(
                    notify(
                        "There was a problem deleting your profile. Please email **support@joyapp.com** and we'll do it for you.",
                        {type: "error", permanent: true},
                    ),
                );
            } else {
                dispatch(
                    notify(
                        "Your account was permanently deleted. Thanks for using Joy.",
                    ),
                );
                const router = await import("@client/router");
                router.push("logout.index", {});
            }
        }
    };

const {verifyRequest} = new (class {
    outstanding: ReturnType<typeof server.core.payments.validate_iap> | null = null;

    verifyRequest = async (payload: {
        appStoreReceipt: string;
        pendingRegistration:
            | models["journal.day.Method"]
            | models["core.rpc.MFPRegistration"]
            | null;
        dispatch: AppDispatch;
    }) => {
        if (this.outstanding == null) {
            console.log("DEDUPED REQUEST");

            if (payload.pendingRegistration != null) {
                const result =
                    "type" in payload.pendingRegistration
                        ? await server.core.rpc.mfp_registration(
                              payload.pendingRegistration,
                          )
                        : await server.journal.day.method(payload.pendingRegistration);
                console.log("RESULT", result);
                if (result.type == "success") {
                    payload.dispatch({
                        type: "SET_PENDING_REGISTRATION",
                        pendingRegistration: null,
                    });

                    const profile = result.data;
                    void analytics.registered(
                        profile.user.tracking_key,
                        "method",
                        "iap",
                    );
                    analytics.unregister("pendingRegistration");
                    void payload.dispatch(auth.postLogin(profile, true));
                    const router = await import("@client/router");
                    router.push("base.index", {});
                }
            }
            this.outstanding = server.core.payments.validate_iap({
                receipt_data: payload.appStoreReceipt,
            });
            void this.outstanding.finally(() => {
                this.outstanding = null;
            });
        }
        return this.outstanding;
    };
})();

export const getSubscriptionProductName = (
    state: RootState,
    plan: "yearly" | "basic",
) => `${state.boot.appId!}.iap.${plan}`;

export const loadAppleSubscriptions =
    () => async (dispatch: AppDispatch, getState: GetState) => {
        const state = getState();
        const {
            subscriptions: {isLoaded},
        } = state;
        const PRODUCT_NAME = getSubscriptionProductName(state, "basic");
        const PRODUCT_NAME_YEARLY = getSubscriptionProductName(state, "yearly");
        const {applicationId} = await hardware.getAppInfo();

        if (isLoaded || getState().boot.platform !== "ios") {
            return;
        }
        CdvPurchase.store.applicationUsername = state.orm.profile?.user.email;

        CdvPurchase.store.verbosity = CdvPurchase.LogLevel.DEBUG;
        CdvPurchase.store.register([
            {
                id: PRODUCT_NAME,
                type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
                platform: CdvPurchase.Platform.APPLE_APPSTORE,
            },
            {
                id: PRODUCT_NAME_YEARLY,
                type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
                platform: CdvPurchase.Platform.APPLE_APPSTORE,
            },
        ]);
        void CdvPurchase.store.initialize();
        CdvPurchase.store.ready(() =>
            dispatch({
                type: "LOAD_APPLE_SUBSCRIPTIONS",
            }),
        );

        void CdvPurchase.store.when().unverified((res) => {
            const code = res.payload.code;

            if (code === 6778003) {
                const adapter = CdvPurchase.store.getAdapter(
                    CdvPurchase.Platform.APPLE_APPSTORE,
                ) as CdvPurchase.AppleAppStore.Adapter;
                void res.receipt.finish().then(() => adapter.refreshReceipt());
            }
        });

        CdvPurchase.store
            .when()
            .approved((transaction) => void transaction.verify())
            .verified((receipt) => {
                void dispatch(orm.fetchSubscription());
                void receipt.finish();
            });

        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        CdvPurchase.store.validator = async (body, callback) => {
            console.log("JOYIAP VERIFYING IAP", JSON.stringify(body, null, 2));
            const pendingRegistration = getState().orm.pendingRegistration;
            console.log("JOYIAP PENDING REGISTRATION", pendingRegistration);
            const casted =
                body.transaction as CdvPurchase.Validator.Request.ApiValidatorBodyTransactionApple;

            const result = await verifyRequest({
                appStoreReceipt: casted.appStoreReceipt ?? "NOT POPULATED",
                pendingRegistration,
                dispatch,
            });

            if (result.type == "success") {
                console.log("JOYIAP", result.data);
                const errorOrNull = result.data;

                if (errorOrNull == null) {
                    callback({
                        ok: true,
                        data: {
                            collection: [{id: body.id!}],
                            id: applicationId,
                            ineligible_for_intro_price: [],
                            latest_receipt: true,
                            transaction:
                                {} as CdvPurchase.Validator.Response.NativeTransaction,
                        },
                    });
                } else {
                    callback({ok: false, code: errorOrNull});
                }
            } else {
                callback({ok: false});
            }
        };
    };

let handler = null as StripeCheckoutHandler | null;

export const loadStripe = () => (dispatch: AppDispatch, getState: GetState) => {
    const subscription = orm.getSubscription(getState());

    const {
        orm: {apiCredentials},
        subscriptions: {isLoaded},
        boot,
    } = getState();

    if (
        isLoaded ||
        // Subscribed through web or hire registration people still need access to stripe.
        (subscription?.type != "stripe" && boot.platform === "ios")
    ) {
        return;
    }

    dispatch({
        type: "LOAD_STRIPE_REQUEST",
    });

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.onload = () => {
        handler = StripeCheckout.configure({
            key: apiCredentials!.STRIPE_PUBLISHABLE_KEY,
        });
        dispatch({
            type: "LOAD_STRIPE_SUCCESS",
        });
    };
    script.src = "https://checkout.stripe.com/checkout.js";
    document.getElementsByTagName("head")[0].appendChild(script);
};

const subscribe = () => (dispatch: AppDispatch, getState: GetState) => {
    if (!handler) {
        throw Error("Credentials are not ready yet.");
    }

    dispatch({
        type: "SUBSCRIBING",
    });

    const profile = orm.getProfile(getState())!;
    const credentials = orm.getCredentials(getState())!;

    handler.open({
        name: "Joy",
        image: `${utils.getBaseURLWithOriginForCapacitor()}/static/favicon.png`,
        description: `Monthly recurring subscription`,
        amount: credentials.SUBSCRIPTION_DOLLARS_PER_MONTH * CENTS,
        email: profile.user.email,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        token: async (payload) => {
            try {
                await dispatch(orm.createSubscription({token: payload.id}));
                dispatch(notify("Thank you for subscribing!"));
            } catch (errors) {
                dispatch({
                    type: "ERROR_SUBSCRIBING",
                });
                const message = get(errors, "data.message") as string | undefined;
                dispatch(
                    notify(message ?? "There was an error processing your payment", {
                        type: "error",
                    }),
                );
            }
        },
    });
};

const unsubscribe = () => (dispatch: AppDispatch) => {
    return dispatch(orm.deleteSubscription());
};

export const confirmPayPal =
    (payload: {payer_id: string; payment_id: string}) =>
    async (dispatch: AppDispatch) => {
        const result = await server.core.payments.create_paypal_subscription(payload);
        if (result.type == "success") {
            dispatch(notify("Thank you for your purchase."));
        } else {
            dispatch(
                notify(
                    "There was an error processing your PayPal payment. Please contact us at support@joyapp.com.",
                    {type: "error"},
                ),
            );
        }
    };

/**
 * Purchase a subscription. In E2E tests with __E2E_BYPASS_IAP__, this skips
 * CdvPurchase and directly calls verifyRequest to process the pending registration.
 */
export const purchaseSubscription =
    (productName: string) => async (dispatch: AppDispatch, getState: GetState) => {
        if (window.__E2E_BYPASS_IAP__ === true) {
            await verifyRequest({
                appStoreReceipt: "E2E_TEST_BYPASS",
                pendingRegistration: getState().orm.pendingRegistration,
                dispatch,
            });
            return undefined;
        }
        return CdvPurchase.store.get(productName)?.getOffer()?.order();
    };

export const actions = {
    confirmPayPal,
    loadStripe,
    showPayPal,
    subscribe,
    unsubscribe,
    loadAppleSubscriptions,
    confirmAndDeleteProfile,
    purchaseSubscription,
};
